import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { integrationResourceKeys } from "@/lib/query";

export type LinkedResource = {
  nymblPersonId: string;
  resourceId: string;
  name: string;
  isActive: boolean;
  lastSyncedAt: string;
};

export type IntegrationResourcesResponse = {
  configured: boolean;
  linked: LinkedResource[];
};

export type RefreshResourcesResult = {
  fetched: number;
  created: number;
  updated: number;
  skipped: number;
  conflicts: number;
  errors: number;
};

async function jsonFetch<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, init);
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed: ${res.status}`);
  }
  return (await res.json()) as T;
}

export function useIntegrationResources() {
  return useQuery({
    queryKey: integrationResourceKeys.list(),
    queryFn: () => jsonFetch<IntegrationResourcesResponse>("/api/integrations/resources"),
  });
}

export function useRefreshResources() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      jsonFetch<RefreshResourcesResult>("/api/integrations/resources/refresh", { method: "POST" }),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: integrationResourceKeys.all });
      toast.success(
        `Refreshed — ${r.fetched} fetched, ${r.created} new, ${r.updated} updated` +
          (r.conflicts > 0 ? `, ${r.conflicts} conflicts` : "") +
          (r.errors > 0 ? `, ${r.errors} errors` : ""),
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
